import React, { useEffect } from 'react'
import { Swap } from '@modules/Swap'
import { TransactionDetails } from '@modules/Transactions'
import {
  Navigate,
  Outlet,
  RouteObject,
  createHashRouter,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom'
import { getPageInfo } from '@plug-core/utils/getPageInfo'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import { ROUTES } from '@utils/routes'
import Authorize from '@modules/Authorize'
import { TokenDetails } from '@modules/Tokens'
import Settings from '@modules/Settings'
import { NftDetails } from '@modules/Nfts/Components/NftDetails'
import { TokenManager } from '@modules/Tokens/Components/TokenManager'
import AnimatedRoute from '@components/AnimatedRoute'
import HomeTabNavigator from '@modules/HomeTabNavigator'
import ResponsiveLayout from '@components/ResponsiveLayout'
import Addresses from '@modules/Home/Components/Addresses'
import { Stack } from '@mui/material'
import i18next from 'i18next'
import Send from '@modules/Send'
import { Contacts } from '@modules/Contacts'
import AccountList from '@modules/AccountList'
import WalletImportTypePicker from '@modules/Wallet/Components/WalletImportTypePicker'
import KeyRevealAcceptance from '@modules/KeyRevealAcceptance'
import ConnectedApps from '@modules/ConnectedApps'
import Toast from '@components/Toast'
import { usePendingRequests } from '@plug-core/state'
import usePrevious from '@hooks/usePrevious'
import { logger } from '@plug-core/logger'
import DeveloperSettings from '@modules/Settings/Components/DeveloperSettings'
import WalletImportPrivateKey from '@modules/Wallet/Components/WalletImportPrivateKey'
import ProviderPicker from '@modules/SwapFallback/ProviderPicker'
import PairPicker from '@modules/SwapFallback/PairPicker'
import ICPSwapFallback from '@modules/SwapFallback/ICPSwapFallback'
import ConvertResult from '@modules/Convert/Components/ConvertResult'
import { Convert } from '@modules/Convert'
import NetworkList from '@modules/Settings/Components/NetworkList'
import NetworkForm from '@modules/Settings/Components/NetworkForm'
import LocaleSettings from '@modules/LocaleSettings'
import WalletImportStoic from '@modules/Wallet/Components/WalletImportStoic'
import MintCK from '@modules/MintCK'
import useWallet from '@plug-core/hooks/useWallet'
import LedgerScannerClient from '@plug-core/clients//LedgerScannerClient'
import { SwapDetails } from '@modules/Transactions/Components/SwapDetails'
import ContactDetails from '@components/ContactDetails'
import LLMChat from '@modules/LLMChat'
import { WalletSetupBackupSeed } from '@modules/WalletSetup/components/BackupSeed'
import { WaterNeuronContainer } from '@modules/WaterNeuron'
import { CanisterTopupPage } from '@modules/TopUp'
import MintCycles from '@modules/MintCycles'
import { TopUpHistory } from '@modules/TopUp/TopUpHistory'
import { BridgeOdin } from '@modules/BridgeOdin'
import BridgeOneSec from '@modules/BridgeOneSec'
import { BridgeHistory } from '@modules/BridgeOneSec/BridgeHistory'

const RootHandler = () => {
  return null
}

const ChildRoute = (props: React.PropsWithChildren) => (
  <Stack
    position="absolute"
    height="100%"
    width="100%"
    top={0}
    overflow="scroll"
    bgcolor={(theme) => theme.palette.background.default}
    alignItems="center"
  >
    <Stack maxWidth="600px" width="100%" height="100%">
      {props.children}
    </Stack>
  </Stack>
)

export const ROUTE_DATA: RouteObject[] = [
  {
    path: ROUTES.ROOT,
    element: <RootHandler />,
    index: true,
  },
  {
    path: ROUTES.HOME,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <ResponsiveLayout>
          <HomeTabNavigator />
        </ResponsiveLayout>
        <Outlet />
      </AnimatedRoute>
    ),
    children: [
      {
        path: ROUTES.CONVERT,
        element: (
          <ChildRoute>
            <AnimatedRoute title="Convert" animationDirection="up">
              <Convert />
              <Outlet />
            </AnimatedRoute>
          </ChildRoute>
        ),
        children: [
          {
            path: ROUTES.CONVERT_RESULT,
            element: (
              <ChildRoute>
                <AnimatedRoute animationDirection="right">
                  <ConvertResult />
                </AnimatedRoute>
              </ChildRoute>
            ),
          },
        ],
      },
      {
        path: ROUTES.TOKEN_DETAILS(':tokenId'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="up" navbar={false}>
              <TokenDetails />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.MINT_CK(':tokenId'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right" navbar={false}>
              <MintCK />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.MANAGE_TOKENS,
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="up" title={i18next.t('manageTokens.title')}>
              <TokenManager />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.SEND,
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="up" navbar={false}>
              <Send />
              <Outlet />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.CONTACTS,
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="up" title="Contacts">
              <Contacts />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.SWAP,
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="up" title="Swap" navbar={false}>
              <Swap />
              <Outlet />
            </AnimatedRoute>
          </ChildRoute>
        ),
        children: [
          {
            path: ROUTES.SWAP_FALLBACK_PROVIDER_SELECT,
            element: (
              <ChildRoute>
                <AnimatedRoute animationDirection="right" title="Select Swap Provider">
                  <ProviderPicker />
                </AnimatedRoute>
              </ChildRoute>
            ),
          },
          {
            path: ROUTES.SWAP_FALLBACK_PAIR_SELECT,
            element: (
              <ChildRoute>
                <AnimatedRoute animationDirection="right" title="Select Swap Pair">
                  <PairPicker />
                </AnimatedRoute>
              </ChildRoute>
            ),
          },
          {
            path: ROUTES.ICPSWAP_FALLBACK_PAIR,
            element: (
              <ChildRoute>
                <AnimatedRoute animationDirection="right" title="Fallback">
                  <ICPSwapFallback />
                </AnimatedRoute>
              </ChildRoute>
            ),
          },
        ],
      },
      {
        path: ROUTES.NFT_DETAILS(':nftCanisterId', ':tokenId'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right">
              <NftDetails />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.CONTACT_DETAILS(':address'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right">
              <ContactDetails />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.TRANSACTION_DETAILS(':transactionId', ':tokenSymbol', ':tokenCanisterId'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right">
              <TransactionDetails />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.SWAP_DETAILS(':tokenFromSymbol', ':tokenToSymbol'),
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right">
              <SwapDetails />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
      {
        path: ROUTES.ADDRESSES,
        element: (
          <ChildRoute>
            <AnimatedRoute animationDirection="right" title="Receive">
              <Addresses />
            </AnimatedRoute>
          </ChildRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.BACKUP_SEED,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <WalletSetupBackupSeed />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.AUTHORIZE,
    element: (
      <AnimatedRoute animationDirection="base" navbar={false}>
        <Authorize />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.SETTINGS,
    element: (
      <ChildRoute>
        <AnimatedRoute animationDirection="up" title="Settings">
          <Settings />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.ACCOUNTS,
    element: (
      <ChildRoute>
        <AnimatedRoute animationDirection="up" title="Accounts">
          <AccountList />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.WALLET_IMPORT_TYPE,
    element: (
      <ChildRoute>
        <AnimatedRoute animationDirection="right" title="Add another wallet">
          <WalletImportTypePicker />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.KEY_REVEAL_ACCEPTANCE,
    element: (
      <ChildRoute>
        <AnimatedRoute animationDirection="right">
          <KeyRevealAcceptance />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.STAKE,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <ResponsiveLayout>
          <WaterNeuronContainer />
        </ResponsiveLayout>
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.TOPUP,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <ResponsiveLayout>
          <CanisterTopupPage />
        </ResponsiveLayout>
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.TOPUP_HISTORY,
    element: (
      <ChildRoute>
        <AnimatedRoute title="Top Up History" animationDirection="right">
          <TopUpHistory />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.MINT_CYCLES,
    element: (
      <ChildRoute>
        <AnimatedRoute title="Mint Cycles" animationDirection="right">
          <MintCycles />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.CONNECTED_APPS,
    element: (
      <ChildRoute>
        <AnimatedRoute title="Connected dApps" animationDirection="right">
          <ConnectedApps />
        </AnimatedRoute>
      </ChildRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS,
    element: (
      <AnimatedRoute title="Developer Settings" animationDirection="right">
        <DeveloperSettings />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_NETWORKS,
    element: (
      <AnimatedRoute title="Networks" animationDirection="right">
        <NetworkList />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_NEW_NETWORK,
    element: (
      <AnimatedRoute title="New Network" animationDirection="right">
        <NetworkForm />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEVELOPER_SETTINGS_EDIT_NETWORK(':networkId'),
    element: (
      <AnimatedRoute title="Edit Network" animationDirection="right">
        <NetworkForm />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.LOCALE_SETTINGS,
    element: (
      <AnimatedRoute title="Select currency" animationDirection="right">
        <LocaleSettings />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.PEM_IMPORT,
    element: (
      <AnimatedRoute title="Import Private Key" animationDirection="right">
        <WalletImportPrivateKey />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.STOIC_IMPORT,
    element: (
      <AnimatedRoute title="Import Stoic wallet" animationDirection="right">
        <WalletImportStoic />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.LLM_CHAT,
    element: (
      <AnimatedRoute title="Plug Intelligence" animationDirection="right">
        <LLMChat />
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.DEPOSIT_ODIN,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <ResponsiveLayout>
          <BridgeOdin />
        </ResponsiveLayout>
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.BRIDGE,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <ResponsiveLayout>
          <BridgeOneSec />
        </ResponsiveLayout>
      </AnimatedRoute>
    ),
  },
  {
    path: ROUTES.BRIDGE_HISTORY,
    element: (
      <AnimatedRoute animationDirection="right" navbar={false}>
        <ResponsiveLayout>
          <BridgeHistory />
        </ResponsiveLayout>
      </AnimatedRoute>
    ),
  },
]

const RootLayout = () => {
  const { fireEvent } = useGoogleAnalytics()
  const { key, pathname } = useLocation()
  const navigate = useNavigate()

  const { wallet } = useWallet()

  const { pendingRequests } = usePendingRequests()
  const prevPendingRequest = usePrevious(pendingRequests?.[0])

  useEffect(() => {
    if (pendingRequests?.[0] && pendingRequests?.[0].id !== prevPendingRequest?.id) {
      navigate(ROUTES.AUTHORIZE)
    }
  }, [navigate, pendingRequests, prevPendingRequest?.id])

  useEffect(() => {
    fireEvent(GAEventName.PAGE_VIEW, getPageInfo(ROUTES, pathname))
  }, [pathname])

  useEffect(() => {
    LedgerScannerClient.registerUser(wallet.principalId, wallet.accountId).catch((e) => {
      if (!e.toString().includes('User already exists')) {
        logger.debug(e)
      }
    })
  }, [wallet])

  if (pathname === ROUTES.ROOT) {
    return <Navigate to={ROUTES.HOME} replace />
  }

  return (
    <>
      <Outlet key={key} />
      <Toast />
    </>
  )
}

export const Rerouter = ({ root }: { root: string }) => {
  const navigate = useNavigate()
  const error = useRouteError() as Error

  useEffect(() => {
    error && logger.error('Error Boundary', { originalError: error })
  }, [error])

  React.useEffect(() => {
    navigate(root)
  }, [])

  return null
}

export const router = createHashRouter([
  {
    element: <RootLayout />,
    children: ROUTE_DATA,
    errorElement: <Rerouter root={ROUTES.ROOT} />,
  },
])
